import { Box, Typography } from '@mui/material'
import { HoldReaction, HoldStatus, JourneyCategory } from '@planning/app/api'
import { getHoldStatusColor } from '@planning/components/hold/hold.utils'
import { ContainerJourneyStatusChip } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { columnFilterPresets } from '@planning/pages/VesselVisit/tableConfigs/columnFilterConfigs'
import { useTranslate } from '@tolgee/react'

import {
    ColoredLabel,
    OptionsButton,
    TomTable,
    TomTableActionsProps,
    TomTableDataExportProps,
    useDefaultMRTOptions,
    useMinimalsTheme,
    useTomTable,
} from '@tom-ui/ui'
import { appStore, useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
    exportTableToCsv,
    exportTableToXlsx,
    IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { HoldsTableData } from '../models/table.model'
import { HoldsViewStore } from '../stores/HoldsViewStore'
import { HoldsAuditDetailPanel } from './HoldsAuditDetailPanel'

interface HoldsTableProps {
  viewStore: HoldsViewStore
}
export const HoldsTable = observer(({ viewStore }: HoldsTableProps) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const theme = useMinimalsTheme()

  const dataExportConfig: IDataExportConfig<HoldsTableData> = {
    id: { hide: true },
    journeyId: { hide: true },
    holdStatus: { hide: true },
    age: { hide: true },
    type: { header: t('type', 'Type'), type: 'string' },
    created: { header: t('createdOn', 'Created on'), type: 'string' },
    containerNumber: { header: t('containerNo', 'Container no.'), type: 'string' },
    journeyStatus: { header: t('Status', 'Status'), type: 'string' },
    category: { header: t('category', 'Category'), type: 'string' },
    cargoStatus: { header: t('cargoStatus', 'Cargo status'), type: 'string' },
    isoCode: { header: t('isoCode', 'ISO Code'), type: 'string' },
    operator: { header: t('operator', 'Operator'), type: 'string' },
  }

  const dataExport: TomTableDataExportProps<HoldsTableData> = {
    title: 'Holds',
    enableDataExport: true,
    dataExportConfig,
    customExportXlsx: table =>
      exportTableToXlsx(
        'Holds',
        table,
        dataExportConfig,
        `holds_${dayjs().format('YYYY-MM-DD')}.xlsx`,
      ),
    customExportCsv: table =>
      exportTableToCsv(
        'Holds',
        table,
        dataExportConfig,
        `holds_${dayjs().format('YYYY-MM-DD')}.csv`,
      ),
  }

  const defaultOptions = useDefaultMRTOptions<HoldsTableData>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('planning/holds')

  const bulkResolve = async (selectedRows: MRT_Row<HoldsTableData>[]) => {
    const firstType = selectedRows[0]?.original.type
    if (selectedRows.every(row => row.original.type === firstType)) {
      const ids = selectedRows.map(row => row.original.id)
      viewStore.openDialog(ids)
    } else {
      const uniqueTypes = Array.from(new Set(selectedRows.map(row => row.original.type)))
      let types = ''
      if (uniqueTypes.length === 1) {
        types = uniqueTypes[0]
      } else if (uniqueTypes.length === 2) {
        types = uniqueTypes.join(' and ')
      } else if (uniqueTypes.length > 2) {
        types = uniqueTypes.slice(0, -1).join(', ') + ' and ' + uniqueTypes[uniqueTypes.length - 1]
      }
      appStore.setShowAlert(
        'error',
        t('holds.bulkResolve.error', `You’ve selected {types}. Please choose one to resolve.`, {
          types,
        }),
      )
    }
  }

  // TODO: add other columns
  const uniqueHoldTypes = viewStore.uniqueHoldTypes

  const columns = useMemo<MRT_ColumnDef<HoldsTableData>[]>(
    () => [
      {
        accessorKey: 'holdStatus',
        header: t('holdStatus', 'Hold status'),
        minSize: 190,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.customSpacing.xs }}>
            <ColoredLabel
              color={getHoldStatusColor(row.original.holdStatus)}
              label={row.original.holdStatus}
              sx={{ height: theme.customSpacing.l }}
            />
          </Box>
        ),
        filterVariant: 'multi-select',
        filterSelectOptions: Object.values(HoldStatus),
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue?.length) return true
          return filterValue.includes(row.getValue(columnId))
        },
        enableColumnFilterModes: false,
      },
      {
        accessorKey: 'type',
        header: t('type', 'Type'),
        size: 100,
        Cell: ({ renderedCellValue }) => (
          <Typography variant='captionBold'>{renderedCellValue}</Typography>
        ),
        filterVariant: 'multi-select',
        filterSelectOptions: uniqueHoldTypes,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue?.length) return true
          return filterValue.includes(row.getValue(columnId))
        },
        enableColumnFilterModes: false,
      },
      {
        id: 'created',
        accessorFn: row => row.createdRaw,
        header: t('createdOn', 'Created on'),
        size: 100,
        Cell: ({ row }) => <Typography variant='caption'>{row.original.created}</Typography>,
        ...columnFilterPresets.dateOptional<HoldsTableData>(),
        enableGlobalFilter: false,
      },
      {
        id: 'age',
        accessorFn: row => row.ageInHours,
        header: t('age', 'Age'),
        size: 100,
        Cell: ({ row }) => <Typography variant='caption'>{row.original.age}</Typography>,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.createdRaw ? new Date(rowA.original.createdRaw).getTime() : 0
          const b = rowB.original.createdRaw ? new Date(rowB.original.createdRaw).getTime() : 0
          return a - b
        },
        ...columnFilterPresets.numericMandatory<HoldsTableData>(),
      },
      {
        accessorKey: 'containerNumber',
        header: t('containerNo', 'Container no.'),
        size: 100,
        maxSize: 100,
        Cell: ({ renderedCellValue }) => (
          <Typography variant='captionBold'>{renderedCellValue}</Typography>
        ),
        ...columnFilterPresets.searchOptional<HoldsTableData>(),
      },
      {
        accessorKey: 'journeyStatus',
        header: t('Status', 'Status'),
        size: 150,
        Cell: cell => {
          const item = cell.row.original

          return <ContainerJourneyStatusChip status={item.journeyStatus ?? '-'} />
        },
        ...columnFilterPresets.orderStatusMultiSelect<HoldsTableData>(),
      },
      {
        accessorKey: 'category',
        header: t('category', 'Category'),
        size: 100,
        Cell: ({ renderedCellValue }) => (
          <Typography variant='captionBold'>{renderedCellValue}</Typography>
        ),
        filterVariant: 'multi-select',
        filterSelectOptions: Object.values(JourneyCategory),
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue?.length) return true
          return filterValue.includes(row.getValue(columnId))
        },
        enableColumnFilterModes: false,
      },
      {
        accessorKey: 'cargoStatus',
        header: t('cargoStatus', 'Cargo status'),
        minSize: 170,
        Cell: ({ renderedCellValue }) => (
          <Typography variant='captionBold'>{renderedCellValue}</Typography>
        ),
        ...columnFilterPresets.fullEmptySelect<HoldsTableData>(),
      },
      {
        accessorKey: 'isoCode',
        header: t('isoCode', 'ISO Code'),
        size: 100,
        Cell: ({ renderedCellValue }) => (
          <Typography variant='captionBold'>{renderedCellValue}</Typography>
        ),
        ...columnFilterPresets.searchMandatory<HoldsTableData>(),
      },
      {
        accessorKey: 'operator',
        header: t('operator', 'Operator'),
        size: 100,
        Cell: ({ renderedCellValue }) => (
          <Typography variant='captionBold'>{renderedCellValue}</Typography>
        ),
        ...columnFilterPresets.searchMandatory<HoldsTableData>(),
      },
    ],
    [t, uniqueHoldTypes],
  )

  const tableActions: TomTableActionsProps<HoldsTableData>[] = [
    {
      label: t('resolve', 'Resolve'),
      onClick: bulkResolve,
    },
  ]

  const table = useTomTable<HoldsTableData>(
    {
      columns,
      data: viewStore.items,
      getRowId: row => String(row.id),
      enableRowSelection: true,
      enableExpanding: true,
      getRowCanExpand: () => true,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: ['mrt-row-actions'],
        },
        columnFilters: [{ id: 'holdStatus', value: [HoldStatus.Active] }],
        sorting: [{ id: 'age', desc: true }],
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => {
        const isActive = row.original.holdStatus === HoldStatus.Active
        const hasBlockRestrictions =
          row.original.restrictions?.some(r => r.reaction === HoldReaction.Block) ?? false
        const canOverride = isActive && hasBlockRestrictions

        return (
          <Box px={theme.customSpacing.s} py={theme.customSpacing.xxs}>
            <OptionsButton
              tooltip={t('moreOptions', 'More options')}
              position='right'
              options={[
                {
                  label: t('resolve', 'Resolve'),
                  onClick: () => viewStore.openDialog([row.original.id]),
                  testId: 'resolve-hold-btn',
                },
                ...(canOverride
                  ? [
                      {
                        label: t('override', 'Override'),
                        onClick: () => viewStore.openOverrideDialog(row.original.id),
                        testId: 'override-hold-btn',
                      },
                    ]
                  : []),
              ]}
            />
          </Box>
        )
      },
      renderDetailPanel: ({ row }) => (
        <HoldsAuditDetailPanel holdId={row.original.id} viewStore={viewStore} />
      ),
    },
    tableActions,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} tableName='HoldsTable' />
})
